import React, { useContext } from "react";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_ROLES_AND_ACCESS,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation,
  PAGE_ROUTE_SOPS
} from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import { DASHBOARD_SOP_SECTIONS } from "./constants/dashboardSopContent";
import { DASHBOARD_SOP_IMAGES } from "./constants/dashboardSopImages";
import type { DashboardSopImageConfig } from "./types";
import {
  publicInfoBodySx,
  publicInfoGuideSectionSx,
  publicInfoLinkSx,
  publicInfoOrderedListSx,
  publicInfoSectionTitleWithImageSx,
  userGuideSectionImageSx
} from "./constants/publicInfoPageStyles";

const DashboardSopImage: React.FC<{
  image: DashboardSopImageConfig;
  testId: string;
}> = ({ image, testId }) => {
  const { t } = useKivunovaTranslation();

  return (
    <Box
      component="img"
      src={image.src}
      alt={t(image.altKey, image.altFallback)}
      data-testid={testId}
      sx={userGuideSectionImageSx}
    />
  );
};

const DashboardSopPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const runBooksPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_SOPS, language, defaultLang)
      : `/${PAGE_ROUTE_SOPS}`;
  const rolesPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_ROLES_AND_ACCESS, language, defaultLang)
      : `/${PAGE_ROUTE_ROLES_AND_ACCESS}`;
  const userGuidePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_USER_GUIDE, language, defaultLang)
      : `/${PAGE_ROUTE_USER_GUIDE}`;
  const contactPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_CONTACT, language, defaultLang)
      : `/${PAGE_ROUTE_CONTACT}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="dashboard-sop-page"
        eyebrow={t("dashboard-sop-page-eyebrow", "Run books")}
        title={t("dashboard-sop-page-title", "How to use the dashboard")}
        intro={t(
          "dashboard-sop-page-intro",
          "See how your business is performing at a glance. Filter by store and date, then read sales, stock, team, and subscription summaries."
        )}
      />

      <PublicInfoCallout testId="dashboard-sop-page-note">
        {t(
          "dashboard-sop-page-note",
          "Company admins and store managers only. Company admins can pick a store from the dropdown. Store managers see their own store automatically. Other roles do not see dashboard data."
        )}
      </PublicInfoCallout>

      <Stack spacing={4} sx={{ mt: 3 }}>
        {DASHBOARD_SOP_SECTIONS.map((section, sectionIndex) => (
          <Box
            key={section.id}
            component="section"
            sx={publicInfoGuideSectionSx}
            data-testid={`dashboard-sop-section-${section.id}`}
          >
            <Typography component="h2" sx={publicInfoSectionTitleWithImageSx}>
              {sectionIndex + 1}. {t(section.titleKey, section.titleFallback)}
            </Typography>
            {section.introKey != null ? (
              <Typography
                variant="body1"
                sx={{ ...publicInfoBodySx, mb: 2 }}
                data-testid={`dashboard-sop-intro-${section.id}`}
              >
                {t(section.introKey, section.introFallback ?? "")}
              </Typography>
            ) : null}
            <Box component="ol" sx={publicInfoOrderedListSx}>
              {section.steps.map((step, stepIndex) => {
                const image =
                  step.imageId != null
                    ? DASHBOARD_SOP_IMAGES[step.imageId]
                    : undefined;

                return (
                  <Box key={`${section.id}-${stepIndex}`} component="li">
                    <Typography
                      component="span"
                      variant="body1"
                      sx={publicInfoBodySx}
                    >
                      {t(step.bodyKey, step.bodyFallback)}
                    </Typography>
                    {image ? (
                      <DashboardSopImage
                        image={image}
                        testId={`dashboard-sop-image-${step.imageId}`}
                      />
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>

      <PublicInfoCtaPanel
        title={t("dashboard-sop-page-cta-title", "Not sure about your role?")}
        testId="dashboard-sop-page-cta"
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          {t(
            "dashboard-sop-page-cta-body",
            "See which roles can open the dashboard and what each role can do across the system."
          )}{" "}
          <MuiLink
            component={Link}
            to={runBooksPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("dashboard-sop-page-cta-all-guides-link", "Run books")}
          </MuiLink>
          {t("dashboard-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={rolesPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("dashboard-sop-page-cta-roles-link", "Roles & access")}
          </MuiLink>
          {t("dashboard-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={userGuidePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("dashboard-sop-page-cta-guide-link", "User guide")}
          </MuiLink>
          {t("dashboard-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("dashboard-sop-page-cta-contact-link", "Contact us")}
          </MuiLink>
        </Typography>
      </PublicInfoCtaPanel>
    </PublicMarketingLayout>
  );
};

export default DashboardSopPage;
